import {
  makeObservable,
  observable,
  action,
  computed,
  IObservableArray,
  onBecomeObserved,
  onBecomeUnobserved,
  runInAction,
  override,
} from "mobx";
import Dexie, { liveQuery, Subscription } from "dexie";
import { BaseSyncModelStore } from "@/store/sync/BaseSyncModelStore";
import { OptimisticSyncUpdate, SyncModelKind } from "@/store/sync/types";
import { AppSubStoreArgs } from "@/store/types";
import type {
  SessionModelData,
  SessionStatus,
  SessionKind,
  SessionConfig,
  SessionPreviousChats,
} from "@/store/sessions/types";
import { SessionObservable } from "./SessionObservable";
import { SyncUpdateValue } from "@/store/sync/types";
import { Maybe } from "@/domains/common/types";
import { uuidModule } from "@/modules/uuid";
import { CreateSessionOperation } from "@/store/sync/operations/sessions/CreateSessionOperation";
import { Uuid } from "@/domains/global/identifiers";
import { UpdateSessionOperation } from "@/store/sync/operations/sessions/UpdateSessionOperation";
import { AppStore } from "@/store/AppStore";
import { SessionIndexes } from "@/store/sessions/SessionIndexes";
import { searchPreviousChatSessions } from "@/store/sessions/search/search-sessions";
import { api } from "@/modules/api";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";
import { clearImportSessionInProgress } from "@/modules/cache/import-session";

export class AppStoreSessionStore extends BaseSyncModelStore<SessionObservable, SessionModelData> {
  sessions: IObservableArray<SessionObservable> = observable.array([]);
  liveQuerySubscription: Maybe<Subscription>;
  private initialLoaded: boolean = false;

  constructor(injectedDeps: AppSubStoreArgs) {
    super({ modelKind: SyncModelKind.Session, ...injectedDeps });
    makeObservable<
      this,
      | "liveQuerySubscription"
      | "subscribeLiveQuery"
      | "unsubscribeLiveQuery"
      | "setIsReady"
      | "initialLoaded"
    >(this, {
      updateSessionOnNewUserMessage: true,
      getPreviousChatSessions: true,
      markSessionAsDismissed: true,
      getLatestInitialChatSession: true,
      getSessionsExceptByStatus: true,
      getSessionsByStatus: true,
      createUserMessageEvent: true,
      processSession: false,
      createSyncModel: false,

      liveQuerySubscription: observable,
      sessions: observable,
      initialLoaded: observable,

      subscribeLiveQuery: action,
      unsubscribeLiveQuery: action,
      setIsReady: action,
      createSession: action,
      cancelImportSession: action,
      processSyncUpdate: override,

      isReady: computed,
      allSessions: computed,
      computeIndexes: override,
    });

    onBecomeObserved(this, "sessions", () => this.subscribeLiveQuery());
    onBecomeUnobserved(this, "sessions", () => this.unsubscribeLiveQuery());
    onBecomeObserved(this, "allSessions", () => this.subscribeLiveQuery());
    onBecomeUnobserved(this, "allSessions", () => this.unsubscribeLiveQuery());
  }

  get isReady() {
    return this.initialLoaded;
  }

  get allSessions(): SessionObservable[] {
    return this.sessions;
  }

  getSessionsByStatus(status: SessionStatus): SessionObservable[] {
    return this.sessions.filter(session => session.status === status);
  }

  getSessionsExceptByStatus(status: SessionStatus): SessionObservable[] {
    return this.sessions.filter(session => session.status !== status);
  }

  createSyncModel(data: SyncUpdateValue<SessionModelData>): SessionObservable {
    return new SessionObservable({
      id: data.model_id,
      data,
      store: this.store,
    });
  }

  /**
   * Creates a new session.
   *
   * @returns The ID of the newly created session
   */
  public async createSession({
    title,
    status,
    kind,
    sessionId,
    config,
  }: {
    title?: string;
    status: SessionStatus;
    kind: SessionKind;
    sessionId?: Uuid;
    config?: SessionConfig;
    lastUserActionAt?: string;
  }): Promise<Uuid> {
    const id = sessionId || uuidModule.generate();

    // Ensure config has required schema_version if provided
    const normalizedConfig = config
      ? {
          ...config,
          schema_version: config.schema_version || ("V1" as const),
        }
      : undefined;

    await new CreateSessionOperation({
      store: this.store,
      payload: {
        id,
        title,
        status,
        kind,
        config: normalizedConfig,
      },
    }).execute();

    return id;
  }

  public async markSessionAsDismissed(sessionId: Uuid) {
    return new UpdateSessionOperation({
      store: this.store,
      payload: { id: sessionId, status: "DISMISSED" },
    }).execute();
  }

  /**
   * Cancel an import session: abort client uploads, mark dismissed, and clear local resume markers.
   */
  public async cancelImportSession(sessionId: Uuid) {
    if (!sessionId) return;

    // Abort local uploads immediately
    try {
      this.store.uploadProgress.cancelUploadsBySession(sessionId);
    } catch {
      // noop
    }

    // Mark session dismissed locally
    try {
      await this.markSessionAsDismissed(sessionId);
    } catch {
      // noop
    }

    // Clear any local in-progress resume markers so refresh won't resume/mark error
    try {
      clearImportSessionInProgress(sessionId);
    } catch {
      // noop
    }

    // Add a message to the session about the cancellation
    try {
      await this.store.sessionEvents.createMessageSentSessionEvent({
        session_id: sessionId,
        message_value: { kind: "REASONING", content: "Import canceled" },
      });
    } catch {
      // noop
    }

    // TODO: add a backend session cancellation endpoint, like so:
    // await api.post(`/v2/sessions/{session_id}/cancel`, {
    //   params: { path: { session_id: sessionId } },
    // });
  }

  /**
   * Updates a session's title
   */
  public async updateSessionOnNewUserMessage(
    sessionId: Uuid,
    {
      title,
      status,
      lastUserActionAt,
    }: {
      title?: string;
      status?: SessionStatus;
      lastUserActionAt?: string;
    }
  ) {
    return new UpdateSessionOperation({
      store: this.store,
      payload: {
        id: sessionId,
        title,
        status,
        last_user_action_at: lastUserActionAt,
      },
    }).execute();
  }

  /**
   * Gets the latest initial chat session that can be used as a placeholder.
   * Returns a session with status INITIAL and kind GENERIC or MEM_IT.
   * If none exists, creates a new one.
   */
  public async getLatestInitialChatSession(): Promise<Uuid> {
    // Look for existing initial sessions
    const initialSessions = await this.localTable
      .where("kind")
      .equals("GENERIC")
      .and(item => item.model_data.status === "INITIAL")
      .sortBy("created_at");

    if (initialSessions.length > 0) {
      // Return the most recently created initial session
      const latestSession = initialSessions[initialSessions.length - 1];
      return latestSession.model_id;
    }

    // No initial session exists, create a new one
    const sessionId = uuidModule.generate();
    return await this.createSession({
      sessionId,
      title: "Mem It Session",
      status: "INITIAL",
      kind: "GENERIC",
      config: {
        schema_version: "V2",
        variant: "STANDARD",
        enable_agent_digests: true,
      },
    });
  }

  /**
   * Creates a user message event for a session
   */
  public async createUserMessageEvent({
    sessionId,
    content,
    context,
  }: {
    sessionId: Uuid;
    content: string;
    context?: Record<string, unknown>;
  }): Promise<void> {
    await this.store.sessionEvents.createSessionEvent({
      session_id: sessionId,
      event_kind: "USER_ACTION_V1",
      event_value: {
        kind: "USER_ACTION_V1",
        parts: [
          {
            kind: "MARKDOWN_V1",
            id: uuidModule.generate(),
            content,
          },
        ],
        context: context || null,
      },
    });
  }

  /**
   * Process/retry a session by setting status to processing and calling the process endpoint
   */
  public async processSession(sessionId: Uuid): Promise<void> {
    if (!sessionId) {
      logger.warn({
        message: "Cannot process session: no session ID provided",
      });

      return;
    }

    try {
      // First update the session status to PROCESSING
      await this.updateSessionOnNewUserMessage(sessionId, {
        status: "PROCESSING",
      });

      // Then call the process session API endpoint
      await api.post(`/v2/sessions/{session_id}/process`, {
        params: { path: { session_id: sessionId } },
        body: {
          immediate: true,
        },
      });
    } catch (error) {
      logger.error({
        message: "Failed to process session",
        info: {
          sessionId,
          error: objectModule.safeErrorAsJson(error as Error),
        },
      });
    }
  }

  private subscribeLiveQuery() {
    this.setIsReady(false);
    this.liveQuerySubscription?.unsubscribe();
    this.liveQuerySubscription = liveQuery(() => {
      // Get all session IDs from the local Dexie table
      return this.localTable.toCollection().primaryKeys();
    }).subscribe({
      next: async ids => {
        const sessions = await this.getManyAsync(ids as string[]);
        runInAction(() => {
          this.sessions.replace(sessions);
          this.setIsReady(true);
        });
      },
    });
  }

  private unsubscribeLiveQuery() {
    this.setIsReady(false);
    this.liveQuerySubscription?.unsubscribe();
  }

  private setIsReady(value: boolean) {
    this.initialLoaded = value;
  }

  public getPreviousChatSessions = (): Promise<SessionPreviousChats[]> => {
    return Dexie.waitFor(searchPreviousChatSessions({ store: this.store }));
  };

  public computeIndexes({
    store,
    remoteData,
    optimisticUpdates,
  }: {
    store: AppStore;
    remoteData: Maybe<SyncUpdateValue<SessionModelData>>;
    optimisticUpdates: OptimisticSyncUpdate<SessionModelData>[];
  }): Record<string, unknown> {
    return new SessionIndexes({ store, remoteData, optimisticUpdates }).indexes;
  }
}
